import { useState } from "react"
import { keepPreviousData } from "@tanstack/react-query"
import { Folder } from "@phosphor-icons/react"
import { useDebounceValue } from "usehooks-ts"

import { Badge, Button, Commandbar } from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import { api } from "~/utils/api"
import { pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { showErrorToast } from "~/utils/toaster"
import { FloatingCommandbar } from "./DraftCommandbar"
import { folderStoreImpact, type FolderStoreImpact } from "./folderStoreImpact"
import { noteRunAfterCurrent, usePackageDraft } from "./setup/setupDrafts"
import type { PackageFolder, RangePackage } from "./types"
import { useAllocationMutations, useAllocationStores } from "./useAllocationData"

const PREVIEW_DELAY_MS = 400

type SaveFolder = ReturnType<typeof useAllocationMutations>["savePackageFolder"]

/** Variant x store pairs the staged stores would add to Overlap, asked for once the planner pauses ticking. */
function useAddedOverlaps(folderId: string, staged: ReadonlySet<string>) {
  const [settled] = useDebounceValue(staged, PREVIEW_DELAY_MS)
  const { data } = api.allocation.previewFolderStoreOverlaps.useQuery(
    { folderId, storeIds: [...settled].toSorted() },
    { placeholderData: keepPreviousData },
  )
  return data?.added ?? 0
}

/** "all 6 packages", but "1 package" on its own. */
const packagesScope = (total: number) => (total === 1 ? "1 package" : `all ${total} packages`)

function titleOf(impact: FolderStoreImpact) {
  const total = impact.rows.length
  if (total === 0) return "Stores for this folder"
  if (impact.changing === 0) return "Stores · no package changes yet"
  return `Stores apply to ${packagesScope(total)} · ${impact.changing} will change`
}

function noteOf(impact: FolderStoreImpact) {
  if (impact.rows.length === 0) {
    return "No packages here yet. Packages you add follow these stores. Takes effect immediately."
  }
  if (impact.replaced === 0) {
    return "Every package in this folder gets exactly these stores. Takes effect immediately."
  }
  const own = impact.replaced === 1 ? "its own set" : "their own set"
  return `${pluralize(impact.replaced, "package")} with ${own} will be replaced. Every package ends up with exactly these stores. Takes effect immediately.`
}

function changeTitle(gained: string[], lost: string[], storeName: (id: string) => string) {
  const lines = [
    gained.length > 0 && `Gains: ${gained.map(storeName).join(", ")}`,
    lost.length > 0 && `Loses: ${lost.map(storeName).join(", ")}`,
  ].filter(Boolean)
  return lines.length > 0 ? lines.join("\n") : "No change"
}

/** Per package: where its stores come from, stores before and after, gained and lost. */
function FolderImpactList({ impact }: { impact: FolderStoreImpact }) {
  const { stores } = useAllocationStores()
  const names = new Map(stores.map((store) => [store.id, store.storeNumber]))
  const storeName = (storeId: string) => names.get(storeId) ?? storeId
  return (
    <div className="min-w-0 flex-1 px-3.5 py-2.5">
      <div className="label-sm mb-1.5 text-fg-muted">Packages</div>
      <div className="flex max-h-60 flex-col gap-1 overflow-y-auto">
        {impact.rows.map((row) => (
          <Tooltip key={row.id} title={changeTitle(row.gained, row.lost, storeName)}>
            <div className="flex items-baseline gap-2.5">
              <span className="paragraph-xs min-w-0 flex-1 truncate text-fg-subtle">
                {row.name}
              </span>
              <span
                className={cn(
                  "paragraph-xs shrink-0",
                  row.replaced ? "text-fg-warning-text" : "text-fg-muted",
                )}
              >
                {row.source}
              </span>
              <span className="paragraph-xs shrink-0 tabular-nums text-fg-muted">
                {`${row.before} → `}
                <span className="font-semibold text-fg-base">{impact.after}</span>
                {" stores"}
              </span>
              <span className="paragraph-xs w-20 shrink-0 text-right tabular-nums">
                {row.gained.length === 0 && row.lost.length === 0 && (
                  <span className="text-fg-muted">no change</span>
                )}
                {row.gained.length > 0 && (
                  <span className="text-fg-positive-text">{`+${row.gained.length}`}</span>
                )}
                {row.gained.length > 0 && row.lost.length > 0 && " "}
                {row.lost.length > 0 && (
                  <span className="text-fg-negative-text">{`−${row.lost.length}`}</span>
                )}
              </span>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

/** The bottom bar while a folder's stores are staged: what Apply does, Discard and Apply. */
export function FolderStoresToolbar({
  save,
  folder,
  folders,
  inside,
  staged,
  applyDisabledReason,
  onClose,
}: {
  save: SaveFolder
  folder: PackageFolder
  folders: PackageFolder[]
  /** The packages in the folder and its subfolders. */
  inside: RangePackage[]
  staged: ReadonlySet<string>
  /** Why Apply waits, e.g. a package inside has staged store edits. */
  applyDisabledReason: string | undefined
  /** After Discard or a successful Apply. */
  onClose: () => void
}) {
  const [open, setOpen] = useState(false)
  const applying = save.isPending
  const overlaps = useAddedOverlaps(folder.id, staged)
  const raised = usePackageDraft().changes.length > 0
  const impact = folderStoreImpact({ folder, folders, packages: inside, staged })
  const total = impact.rows.length
  const apply = () =>
    save.mutate(
      { op: { kind: "setFolderStores", folderId: folder.id, storeIds: [...staged] } },
      {
        onSuccess: (result) => {
          noteRunAfterCurrent(result.run)
          onClose()
        },
        onError: showErrorToast,
      },
    )
  return (
    <FloatingCommandbar
      raised={raised}
      details={open && total > 0 && <FolderImpactList impact={impact} />}
    >
      <Commandbar inline hideHeader count={1} ariaLabel="Folder stores">
        <Folder size={16} className="shrink-0 text-fg-muted" />
        <div className="flex max-w-[52ch] flex-col">
          <span className="label-sm">{titleOf(impact)}</span>
          <span className="paragraph-xs text-fg-muted">{noteOf(impact)}</span>
        </div>
        {overlaps > 0 && (
          <Badge size="sm" variant="yellow">
            {`${pluralize(overlaps, "variant × store pair")} added to Overlap · the most restrictive rule wins`}
          </Badge>
        )}
        <Button size="xs" variant="neutral" disabled={applying} onClick={onClose}>
          Discard
        </Button>
        {total > 0 && (
          <Button size="xs" variant="neutral" onClick={() => setOpen(!open)}>
            {open ? "Hide details" : "Show details"}
          </Button>
        )}
        <Button
          size="xs"
          requires="edit"
          loading={applying}
          disabled={applyDisabledReason !== undefined}
          allowedTitle={applyDisabledReason}
          onClick={apply}
        >
          {total === 0 ? "Apply" : `Apply to ${packagesScope(total)}`}
        </Button>
      </Commandbar>
    </FloatingCommandbar>
  )
}
